import * as React from 'react';
import { AppBaseButton } from '../AppBaseButton';
import { AppPersonaButtonProps, LivePersonaCardParameters, PersonaInfo } from './AppPersonaButton.Props';
import { getLabelButtonProps } from '../AppBaseButton/getLabelButtonProps';
import { iconNameToIconProps } from '../iconNameToIconProps';
import { deriveButtonKeytip, defaultKeytipExecute } from '../../../utilities';
import { dataUniqueId } from '../../../utilities/dataUniqueId';
import { memoizeFunction } from '@fluentui/react/lib/Utilities';

export class AppPersonaButton extends React.PureComponent<AppPersonaButtonProps, {}> {
  private baseButton = React.createRef<AppBaseButton>();

  public render() {
    return this.getLivePersonaCardUIHost();
  }

  private getLivePersonaCardUIHost = () => {
    const {
      styles,
      onKeyDown,
      disabled,
      icon,
      iconColor,
      hideLabel,
      dataAutomationType,
      id,
      role,
      keytipProps,
      ariaDescription,
      onRenderIcon,
      lang,
      dataNav,
      personaInfo,
      label,
      setSize,
      positionInSet,
      secondaryText,
    } = this.props;

    const LivePersonaCardTarget = this.getLivePersonaCardTarget();

    const baseButton = (
      <AppBaseButton
        secondaryText={secondaryText}
        ref={this.baseButton}
        styles={styles}
        onClick={this.onExecute}
        onFocus={this.onFocus}
        onBlur={this.onBlur}
        onKeyDown={onKeyDown}
        disabled={disabled}
        iconProps={iconNameToIconProps(icon, iconColor)}
        {...getLabelButtonProps(label, hideLabel)}
        data-automation-type={dataAutomationType || 'AppPersonaButton'}
        data-unique-id={dataUniqueId(this.props)}
        role={role}
        keytipProps={deriveButtonKeytip(keytipProps, this.keytipExecute)}
        ariaLabel={label}
        ariaDescription={ariaDescription}
        onRenderIcon={onRenderIcon}
        lang={lang}
        id={id}
        data-nav={dataNav}
        aria-setsize={setSize}
        aria-posinset={positionInSet}
      />
    );

    return this.usingLPCReactElement() && !personaInfo.isUnauthenticatedSender ? (
      <LivePersonaCardTarget cardParameters={this.getLivePersonaCardParameters(personaInfo)}>
        {baseButton}
      </LivePersonaCardTarget>
    ) : (
      baseButton
    );
  };

  private getLivePersonaCardTarget = memoizeFunction(
    (): React.ComponentType<{ cardParameters: any }> => (window as any).LivePersonaCardHoverTargetV2
  );

  public focus() {
    this.baseButton && this.baseButton.current && this.baseButton.current.focus();
  }

  private keytipExecute = (executeTarget: HTMLElement) => {
    defaultKeytipExecute(executeTarget, this.executeFromKeytip);
  };

  private usingLPCReactElement = (): boolean => {
    return (
      this.getLivePersonaCardTarget() !== undefined && this.getElementToAnchorOn(this.props.anchorSelector) === null
    );
  };

  private onExecute = () => {
    const { id, onExecute, personaInfo } = this.props;
    if (personaInfo.isUnauthenticatedSender) {
      return;
    }
    if (!this.usingLPCReactElement()) {
      this.openContactCard();
    }
    onExecute && onExecute({ ...personaInfo, id });
  };

  private executeFromKeytip = () => {
    this.onExecute();
  };

  private onFocus = () => {
    this.props?.onFocus?.();
  };

  private onBlur = () => {
    this.props?.onBlur?.();
  };

  // LPC knows its position in two ways:
  //  1. If using the React element <LivePersonaCardHoverTargetV2>, simply wrap the element that will reveal the
  //     LPC in this React element.
  //  2. If NOT using the React element, the two functions exposed for rendering an LPC (addedPersonaToPageV2 and
  //     openCard) take in a DOM element as the first parameter and positions the card relative to this element.
  private openContactCard = () => {
    const { personaInfo, anchorSelector } = this.props;
    if (personaInfo && isLivePersonaCardBootstrapped()) {
      const targetElement = this.getElementToAnchorOn(anchorSelector);
      const parameters = this.getLivePersonaCardParameters(personaInfo);
      targetElement && this.openCard(targetElement, parameters);
    }
  };

  // Provides the expected parameters for the LivePersonaCard. Currently providing the bare minimum with little customization but this can be
  // changed as we determine what other parameters might need to be added on.
  private getLivePersonaCardParameters = (personaInfo: PersonaInfo): LivePersonaCardParameters => {
    return {
      personaInfo,
      behavior: {
        onCardOpen: this.onCardOpen,
        onCardClose: this.props.onCardClose,
        enableStickiness: true,
        locationToOpen: this.props.cardLocationToOpen,
      },
    };
  };

  private onCardOpen = () => {
    this.props.onCardOpen && this.props.onCardOpen({ id: this.props.id });
  };

  // The LPC Bootstrapper works by attaching exposed functions to the window object. This attempts to get one of
  // these functions from window and checks to make sure it exists before using it.
  private openCard = (element: HTMLElement, parameters: LivePersonaCardParameters): void => {
    isLivePersonaCardBootstrapped() &&
      (window as any).LPC.openCard &&
      (window as any).LPC.openCard(element, parameters);
  };

  private getElementToAnchorOn = (selector?: string): HTMLElement | null => {
    return selector ? document.querySelector(selector) : null;
  };
}

export function isLivePersonaCardBootstrapped(): boolean {
  return !!((window as any).LPC && (window as any).LivePersonaCardHoverTargetV2);
}
